/**
 * 救援目标 / 选点标记工厂（从 Dashboard.js 拆出）
 */
import {
  resolveAutoPoint,
  resolveManualPoint,
  hasMeaningfulManualPoint
} from '../pickPointCapture';

export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[character]));
}

/**
 * 创建手机选点标记元素
 * @param {string} color - 颜色（当没有自定义图标时使用）
 * @param {string} title - 悬停提示文字
 * @param {string} [iconUrl] - 自定义图标 URL（优先使用）
 * @returns {HTMLElement}
 */
export function createPickPointElement(color = '#f97316', title = '手机选点', iconUrl = null) {
  const element = document.createElement('div');
  element.title = title;
  
  // 如果有自定义图标，使用图标图片
  if (iconUrl) {
    element.style.cssText = 'width:32px;height:32px;margin:0;padding:0;border:none;background:transparent;line-height:0;cursor:pointer;';
    const img = document.createElement('img');
    img.src = iconUrl.startsWith('/') ? iconUrl : `/api/icons/file/${encodeURIComponent(iconUrl.split('/').pop())}`;
    img.style.cssText = 'width:32px;height:32px;display:block;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4));';
    img.onerror = () => {
      // 图标加载失败时回退到默认水滴
      element.innerHTML = '';
      const inner = document.createElement('div');
      inner.style.cssText = `width:22px;height:22px;border-radius:50% 50% 50% 0;background:#f97316;border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.45);transform:rotate(-45deg);`;
      element.appendChild(inner);
      element.style.width = '22px';
      element.style.height = '22px';
    };
    element.appendChild(img);
    return element;
  }
  
  // 默认水滴标记
  const fill = /^#[0-9a-fA-F]{3,8}$/.test(String(color || '')) ? color : '#f97316';
  element.style.cssText = 'width:22px;height:22px;margin:0;padding:0;border:none;background:transparent;line-height:0;cursor:pointer;';
  const inner = document.createElement('div');
  inner.style.cssText = `width:22px;height:22px;border-radius:50% 50% 50% 0;background:${fill};border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.45);transform:rotate(-45deg);`;
  element.appendChild(inner);
  return element;
}


/** 三次取样：带序号的圆点 */
export function createPickSampleElement(color, seq) {
  const wrap = document.createElement('div');
  const fill = /^#[0-9a-fA-F]{3,8}$/.test(String(color || '')) ? color : '#2563eb';
  wrap.style.cssText = `width:20px;height:20px;border-radius:50%;background:${fill};border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.4);color:#fff;font:700 11px/16px system-ui,sans-serif;text-align:center;cursor:pointer;`;
  wrap.textContent = String(seq || '');
  wrap.title = `第 ${seq} 次取样`;
  return wrap;
}

export function createPickDistanceLabel(text) {
  const element = document.createElement('div');
  element.style.cssText = 'padding:2px 8px;border-radius:10px;background:rgba(15,23,42,.92);color:#fff;font:700 11px/16px system-ui,sans-serif;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.35);pointer-events:none;';
  element.textContent = text;
  return element;
}

/**
 * @param {object} point
 * @param {{ canDelete?: boolean }} [options]
 */
export function buildPickPointPopupHtml(point, options = {}) {
  const note = escapeHtml(point?.note || '手机选点');
  const auto = resolveAutoPoint(point);
  const autoText = auto
    ? `经度 ${auto.lng.toFixed(6)}, 纬度 ${auto.lat.toFixed(6)}`
    : '';
  const autoAcc = auto?.accuracy;
  const manual = resolveManualPoint(point);
  const hasManual = hasMeaningfulManualPoint(auto, manual);
  const when = point?.used_at ? new Date(point.used_at).toLocaleString() : '';
  const id = escapeHtml(String(point?.id ?? ''));
  const canDelete = options.canDelete === true;
  const extraHtml = typeof options.extraHtml === 'string' ? options.extraHtml : '';
  const deleteBtn = canDelete
    ? `<div style="margin-top:8px">
        <button
          type="button"
          data-zhxt-pick-delete="${id}"
          style="padding:3px 10px;font-size:12px;color:#fff;background:#e53935;border:none;border-radius:4px;cursor:pointer"
        >删除此点</button>
      </div>`
    : '';
  const manualLine = hasManual
    ? `<div style="margin-top:4px;color:#0369a1">手工：经度 ${manual.lng.toFixed(6)}, 纬度 ${manual.lat.toFixed(6)}</div>`
    : '';
  const accLine = Number.isFinite(autoAcc)
    ? `<div style="color:#64748b;font-size:12px">自动精度约 ${Math.round(autoAcc)} 米</div>`
    : '';
  return `
    <div style="font-size:13px;min-width:180px">
      <b>手机选点</b><br/>
      ${note}<br/>
      ${autoText ? `自动：${autoText}<br/>` : ''}
      ${accLine}
      ${manualLine}
      ${extraHtml}
      ${when ? `<div style="margin-top:4px;color:#64748b;font-size:12px">${when}</div>` : ''}
      ${deleteBtn}
    </div>
  `;
}

/**
 * 救援水滴标记
 * @param {string} color
 * @param {string} [text] 已废弃：不再画在针上方（易被 line-height:0 压成细线）；改走悬停/弹窗
 * @param {{ badge?: string }} [options] badge 如 'SOS'；空则不显示字
 */
export function createRescueIcon(color, text = '', options = {}) {
  const el = document.createElement('div');
  el.style.width = '28px';
  el.style.height = '36px';
  el.style.margin = '0';
  el.style.padding = '0';
  el.style.border = 'none';
  el.style.background = 'transparent';
  el.style.lineHeight = '0';
  // 勿设 position:relative，会破坏 Mapbox Marker 的 transform 定位（缩放会飘）
  el.style.overflow = 'visible';
  el.style.cursor = 'pointer';

  const fill = /^#[0-9a-fA-F]{3,8}$/.test(String(color || '')) ? color : '#1976d2';
  const badge = options.badge != null
    ? String(options.badge)
    : (text && String(text).trim() === 'SOS' ? 'SOS' : '');

  // 针尖在 viewBox 底边中点 (14,36)，配合 Marker anchor=bottom
  const badgeSvg = badge
    ? `<text x="14" y="14" text-anchor="middle" fill="#fff" font-size="8" font-weight="700" font-family="system-ui,Arial,sans-serif">${escapeHtml(badge)}</text>`
    : `<circle cx="14" cy="13" r="4.2" fill="#fff"/>`;

  el.innerHTML = `
    <svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg" style="display:block;pointer-events:none">
      <path d="M14 1C7.4 1 2 6.4 2 13c0 9.5 12 23 12 23s12-13.5 12-23C26 6.4 20.6 1 14 1z"
        fill="${fill}" stroke="#fff" stroke-width="1.2"/>
      ${badgeSvg}
    </svg>
  `;

  if (text && String(text).trim() && String(text).trim() !== 'SOS') {
    el.title = String(text).trim();
  }

  return el;
}
